// @ts-expect-error type bindings not available
import { formatMoney } from "@shopify/theme-currency"
import "./markets-types"
import {
  Position,
  Product,
  RecProductElementSelectors,
  ResponseJSON,
  Variant,
  VariantOptionSelector
} from "./markets-types"

const MAX_VARIANT_OPTIONS = 5

function constructUrl(productHandle: string, errors: string[]) {
  if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
    return `${window.origin}${window.Shopify.routes.root}products/${productHandle}`
  }
  errors.push("window.Shopify object not found. Can't migrate data on Non-Shopify pages")
  return null
}

async function fetchProductMarketInfo(errors: string[], productHandle: string) {
  if (!productHandle) {
    throw new Error("Product handle is not available")
  }

  const productUrl = constructUrl(productHandle, errors)
  if (productUrl && errors.length === 0) {
    const response = await fetch(`${productUrl}.js`)
    if (!response.ok) {
      throw new Error(`Error fetching product data: [${response.status}] ${response.statusText}`)
    }
    const json = await response.json<Product>()
    return {
      product: json,
      url: productUrl
    }
  }
  throw new Error(errors.join("\n"))
}

function isValidVariantSelector(variantSelector: VariantOptionSelector) {
  if (variantSelector) {
    if (variantSelector.position && (variantSelector.titleElement || variantSelector.optionElement)) {
      return true
    }
  }
  return false
}

function determineDependentVariantOptionsCount(productInfo: Product, totalRequestVariantOptions: number | null) {
  if (
    !totalRequestVariantOptions ||
    totalRequestVariantOptions > MAX_VARIANT_OPTIONS ||
    totalRequestVariantOptions > productInfo.options.length
  ) {
    return productInfo.options.length - 1
  }
  return totalRequestVariantOptions - 1
}

function fetchSelectedVariantValue(productSection: Element, variantSelector: VariantOptionSelector) {
  if (variantSelector.optionElement) {
    const selectedElement = productSection.querySelector(
      `${variantSelector.optionElement}${variantSelector.selectedElement}`
    )
    if (selectedElement) {
      return selectedElement.innerHTML
    }
  }
  return null
}

/**
 * Gets the money format from Nosto's namespace or from the global Shopify namespace
 */
function getMoneyFormat(): string | undefined {
  if (typeof window.Shopify === "object") {
    if (window.Shopify?.nosto?.money_format) {
      return window.Shopify?.nosto?.money_format
    }
    if (window.Shopify?.money_format) {
      return window.Shopify?.money_format
    }
  }
  return undefined
}

function getOption(v: Variant, position: Position | "1" | "2" | "3") {
  return v[`option${position}`]
}

function updatePricingFromVariantSelection(
  productSection: Element,
  elementSelectors: RecProductElementSelectors,
  actualTotalVariantOptions: number,
  selectCombination: Record<Position, string>,
  productInfo: Product,
  errors: string[]
) {
  if (elementSelectors.totalVariantOptions) {
    const totalMissingGivenCount = actualTotalVariantOptions - Object.keys(selectCombination).length
    if (totalMissingGivenCount > 0) {
      errors.push(`Missing ${totalMissingGivenCount} selected options`)
      return
    }
  } else {
    const totalMissingActualCount = productInfo.options.filter(option => !selectCombination[option.position]).length
    if (totalMissingActualCount > 0) {
      errors.push(`Missing ${totalMissingActualCount} selected options`)
      return
    }
  }

  const matched = productInfo.variants.find(variant =>
    Object.keys(selectCombination).every(position => getOption(variant, position) === selectCombination[position])
  )

  if (matched) {
    if (elementSelectors.listPriceElement) {
      const listPriceElement = productSection.querySelector<HTMLElement>(elementSelectors.listPriceElement)
      if (listPriceElement) {
        if (matched.compare_at_price) {
          listPriceElement.innerHTML = formatMoney(matched.compare_at_price, getMoneyFormat())
        } else {
          listPriceElement.style.display = "none"
        }
      }
    }
    if (elementSelectors.priceElement) {
      const priceElement = productSection.querySelector<HTMLElement>(elementSelectors.priceElement)
      if (priceElement) {
        if (matched.price) {
          priceElement.innerHTML = formatMoney(matched.price, getMoneyFormat())
        } else {
          priceElement.style.display = "none"
        }
      }
    }
  }
}

function updatePricingNonVariant(
  productSection: Element,
  elementSelectors: RecProductElementSelectors,
  productInfo: Product,
  errors: string[]
) {
  let variant = productInfo.variants[0]

  if (elementSelectors.defaultVariantIdAttribute) {
    const variantId = productSection.getAttribute(elementSelectors.defaultVariantIdAttribute)
    if (variantId) {
      const matched = productInfo.variants.find(filter => filter.id === parseInt(variantId, 10))
      if (matched) {
        variant = matched
      } else {
        errors.push(
          `Variant id provided ${variantId} with selector ${elementSelectors.defaultVariantIdAttribute} is not found in product ${productInfo.id}`
        )
      }
    } else {
      errors.push(
        `Default variant id not found with selector ${elementSelectors.defaultVariantIdAttribute} for product ${productInfo.id}`
      )
    }
  }

  if (elementSelectors.listPriceElement) {
    const listPriceElement = productSection.querySelector<HTMLElement>(elementSelectors.listPriceElement)
    if (listPriceElement) {
      if (variant.compare_at_price) {
        listPriceElement.innerHTML = formatMoney(variant.compare_at_price, getMoneyFormat())
      } else {
        listPriceElement.style.display = "none"
      }
    }
  }

  if (elementSelectors.priceElement) {
    const priceElement = productSection.querySelector<HTMLElement>(elementSelectors.priceElement)
    if (priceElement) {
      if (variant.price) {
        priceElement.innerHTML = formatMoney(variant.price, getMoneyFormat())
      } else {
        priceElement.style.display = "none"
      }
    }
  }
}

function updateTitle(
  productSection: Element,
  elementSelectors: RecProductElementSelectors,
  productInfo: Product,
  errors: string[]
) {
  if (elementSelectors.productTitleElement) {
    const titleElement = productSection.querySelector(elementSelectors.productTitleElement)
    if (titleElement) {
      titleElement.innerHTML = productInfo.title
    } else {
      errors.push(
        `Can't find title element with selector ${elementSelectors.productTitleElement} for product ${productInfo.id}`
      )
    }
  }
}

function isAnchor(e: HTMLElement): e is HTMLAnchorElement {
  return e.tagName.toLowerCase() === "a"
}

function updateProductUrl(
  productSection: Element,
  elementSelectors: RecProductElementSelectors,
  url: string,
  productInfo: Product,
  errors: string[]
) {
  if (elementSelectors.productUrlElement) {
    const productUrlElements = productSection.querySelectorAll<HTMLElement>(elementSelectors.productUrlElement)
    if (productUrlElements.length > 0) {
      productUrlElements.forEach(element => {
        if (isAnchor(element)) {
          const params = new URL(element.href).search
          element.href = `${url}${params}`
        }
      })
    } else {
      errors.push(
        `Can't find product url elements with selector ${elementSelectors.productUrlElement} for product ${productInfo.id}`
      )
    }
  }
}

function updateDescription(
  productSection: Element,
  elementSelectors: RecProductElementSelectors,
  productInfo: Product,
  errors: string[]
) {
  if (elementSelectors.descriptionElement) {
    const descriptionElement = productSection.querySelector(elementSelectors.descriptionElement)
    if (descriptionElement) {
      descriptionElement.innerHTML = productInfo.description
    } else {
      errors.push(
        `Can't find product description element with selector ${elementSelectors.descriptionElement} for product ${productInfo.id}`
      )
    }
  }
}

function updatePrimaryVariantOption(
  productSection: Element,
  variantSelector: VariantOptionSelector,
  productInfo: Product
) {
  const skuOption = productInfo.options.find(option => option.position === variantSelector.position)
  if (variantSelector.titleElement && skuOption) {
    const titleElement = productSection.querySelector(variantSelector.titleElement)
    if (titleElement) {
      titleElement.innerHTML = skuOption.name
    }
  }

  if (variantSelector.optionElement) {
    const variantElements = productSection.querySelectorAll(variantSelector.optionElement)
    if (variantElements && variantElements.length > 0) {
      const variantOptions = productInfo.variants.map(variant => variant[`option${variantSelector.position}`])

      const uniqueOptions = [...new Set(variantOptions)]
      uniqueOptions.forEach((primaryVariant, index) => {
        variantElements[index].innerHTML = primaryVariant!
      })
    }
  }
}

function updateDependentVariantOption(
  productSection: Element,
  variantSelector: VariantOptionSelector,
  primaryVariantSelectors: VariantOptionSelector[],
  productInfo: Product,
  errors: string[]
) {
  const skuOption = productInfo.options.find(option => option.position === variantSelector.position)
  if (variantSelector.titleElement && skuOption) {
    const titleElement = productSection.querySelector(variantSelector.titleElement)
    if (titleElement) {
      titleElement.innerHTML = skuOption.name
    }
  }

  if (variantSelector.optionElement) {
    const totalMissingSelectors = primaryVariantSelectors.filter(selector => !selector.selectedElement).length
    if (totalMissingSelectors > 0) {
      errors.push(`Missing ${totalMissingSelectors} primary variant selectors`)
    } else {
      const selectedPrimaryVariantOptions = primaryVariantSelectors.map(selector => {
        return {
          position: selector.position,
          value: productSection.querySelector(`${selector.optionElement}${selector.selectedElement}`)?.innerHTML
        }
      })

      const dependentElementValues = productInfo.variants
        .filter(variant =>
          selectedPrimaryVariantOptions.every(
            selectedOption => variant[`option${selectedOption.position}`] === selectedOption.value
          )
        )
        .map(matched => matched[`option${variantSelector.position}`])

      const uniqueOptions = [...new Set(dependentElementValues)]

      const variantElements = productSection.querySelectorAll(variantSelector.optionElement)
      if (variantElements && variantElements.length > 0) {
        uniqueOptions.forEach((value, index) => {
          variantElements[index].innerHTML = value!
        })
        if (variantSelector.selectedElement) {
          const selectedClass = variantSelector.selectedElement.replace(".", "")
          const priorSelectedElement = [...variantElements].filter(element => element.classList.contains(selectedClass))
          if (priorSelectedElement.length === 0) {
            variantElements[0].classList.add(selectedClass)
          }
        }
      }
    }
  }
}

function processDependentVariant(
  productSection: Element,
  primaryVariantSelectors: VariantOptionSelector[],
  selectCombination: Record<number, string>,
  productInfo: Product,
  totalDependentVariantOptions: number,
  errors: string[],
  dependentVariantSelector: VariantOptionSelector
): Record<number, string> {
  if (
    dependentVariantSelector &&
    isValidVariantSelector(dependentVariantSelector) &&
    dependentVariantSelector.selectedElement
  ) {
    updateDependentVariantOption(productSection, dependentVariantSelector, primaryVariantSelectors, productInfo, errors)
    const dependentPosition = dependentVariantSelector.position || 0
    const localSelectCombination: Record<number, string> = { ...selectCombination }
    localSelectCombination[dependentPosition] = fetchSelectedVariantValue(productSection, dependentVariantSelector)!
    if (dependentVariantSelector.dependentVariantSelector && totalDependentVariantOptions > 0) {
      const primaryVariants = [...primaryVariantSelectors]
      primaryVariants.push(dependentVariantSelector)
      return processDependentVariant(
        productSection,
        primaryVariants,
        localSelectCombination,
        productInfo,
        totalDependentVariantOptions - 1,
        errors,
        dependentVariantSelector.dependentVariantSelector
      )
    }
    return localSelectCombination
  }
  return selectCombination
}

function updateVariantOptions(
  productSection: Element,
  elementSelectors: RecProductElementSelectors,
  productInfo: Product,
  errors: string[]
) {
  if (elementSelectors && elementSelectors.variantSelector) {
    const selectCombination: Record<number, string> = {}
    let processedVariantSelection: Record<number, string> = {}
    const { variantSelector } = elementSelectors
    const actualTotalDependentVariantOptions = determineDependentVariantOptionsCount(
      productInfo,
      elementSelectors.totalVariantOptions!
    )
    if (variantSelector.primaryVariantSelector && isValidVariantSelector(variantSelector.primaryVariantSelector)) {
      const { primaryVariantSelector } = variantSelector
      updatePrimaryVariantOption(productSection, primaryVariantSelector, productInfo)
      const primaryPosition = primaryVariantSelector.position
      const primaryVariantValue = fetchSelectedVariantValue(productSection, primaryVariantSelector)
      if (primaryVariantValue) {
        selectCombination[primaryPosition!] = primaryVariantValue
        processedVariantSelection = processDependentVariant(
          productSection,
          [primaryVariantSelector],
          selectCombination,
          productInfo,
          actualTotalDependentVariantOptions,
          errors,
          primaryVariantSelector.dependentVariantSelector!
        )
      } else {
        errors.push(
          `Can't determine selected primary variant with selector ${variantSelector.primaryVariantSelector} for product ${productInfo.id}`
        )
      }
    }

    if (Object.keys(processedVariantSelection).length > 0) {
      updatePricingFromVariantSelection(
        productSection,
        elementSelectors,
        actualTotalDependentVariantOptions + 1,
        processedVariantSelection,
        productInfo,
        errors
      )
    } else {
      errors.push(
        `No variant option selection found with selectors ${JSON.stringify(
          elementSelectors.variantSelector
        )} for product ${productInfo.id}`
      )
    }
  } else if (elementSelectors) {
    updatePricingNonVariant(productSection, elementSelectors, productInfo, errors)
  }
}

function updateCategory(
  productSection: Element,
  elementSelectors: RecProductElementSelectors,
  productInfo: Product,
  errors: string[]
) {
  if (elementSelectors.categoryNameElement) {
    const categoryNameElement = productSection.querySelector(elementSelectors.categoryNameElement)
    if (categoryNameElement) {
      categoryNameElement.innerHTML = productInfo.type
    } else {
      errors.push(
        `Can't find category element with selector ${elementSelectors.categoryNameElement} for product ${productInfo.id}`
      )
    }
  }
}

async function handleProductResponse(
  productSection: Element,
  elementSelectors: RecProductElementSelectors,
  productJsonPromise: Promise<ResponseJSON>,
  errors: string[]
) {
  const { product, url } = await productJsonPromise
  updateTitle(productSection, elementSelectors, product, errors)
  updateProductUrl(productSection, elementSelectors, url, product, errors)
  updateDescription(productSection, elementSelectors, product, errors)
  updateCategory(productSection, elementSelectors, product, errors)
  updateVariantOptions(productSection, elementSelectors, product, errors)
}

function migrateToShopifyMarket(recProductElementSelectors: RecProductElementSelectors) {
  const errors: string[] = []
  if (recProductElementSelectors && recProductElementSelectors.productSectionElement) {
    const productSectionIterator = document.querySelectorAll(recProductElementSelectors.productSectionElement).values()
    const promises = Array.from(productSectionIterator).map(async productSection => {
      const productHandle = productSection.getAttribute(recProductElementSelectors.productHandleAttribute)
      await handleProductResponse(
        productSection,
        recProductElementSelectors,
        fetchProductMarketInfo(errors, productHandle!),
        errors
      )
      if (errors.length > 0) {
        throw new Error(errors.join("\n"))
      }
      return "Success"
    })
    return Promise.all(promises)
  }
  if (!recProductElementSelectors || Object.keys(recProductElementSelectors).length === 0) {
    return Promise.reject(new Error("No element selectors provided"))
  }
  return Promise.reject(new Error("No product selector element provided"))
}

export default migrateToShopifyMarket
